import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useData } from '@/hooks/useData';
import { apiFetch } from '@/services/apiGateway';
import { GlobalHeader } from '@/components/GlobalHeader';

const DAYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

interface FreeTimeSlot {
  day: string;
  start_time: string;
  end_time: string;
  label: string | null;
}

interface FreeTimeResponse {
  slots: FreeTimeSlot[];
}

interface SlotForm {
  day: string;
  start_time: string;
  end_time: string;
  label: string;
}

const emptyForm = (): SlotForm => ({
  day: 'monday',
  start_time: '16:00',
  end_time: '18:00',
  label: '',
});

export function ManageFreeTimePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { childId } = useAuthStore();

  const { data, isLoading } = useData<FreeTimeResponse>(
    childId ? `/children/${childId}/free-time` : null,
  );

  const [localSlots, setLocalSlots] = useState<FreeTimeSlot[]>([]);
  const [form, setForm] = useState<SlotForm>(emptyForm());
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (data?.slots) {
      setLocalSlots(data.slots);
    }
  }, [data]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = () => {
    setLocalSlots((prev) => [
      ...prev,
      {
        day: form.day,
        start_time: form.start_time,
        end_time: form.end_time,
        label: form.label.trim() || null,
      },
    ]);
    setForm(emptyForm());
  };

  const handleRemove = (idx: number) => {
    setLocalSlots((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!childId) return;
    setIsSaving(true);
    try {
      await apiFetch(`/children/${childId}/free-time`, {
        method: 'PUT',
        body: JSON.stringify({ slots: localSlots }),
      });
      showToast(t('manage_free_time.saved_toast'));
      setTimeout(() => navigate('/student/timetable'), 800);
    } catch {
      showToast(t('manage_free_time.save_failed_toast'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student/timetable" />
        <div className="app-content" style={{ paddingBottom: '80px' }}>

          <p style={{ margin: '0 0 2px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--primary)' }}>
            VADOStudy
          </p>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 20px' }}>
            {t('manage_free_time.title')}
          </h1>

          {/* ── Loading skeleton ── */}
          {isLoading && (
            <div className="loading-skeleton" style={{ height: '160px', borderRadius: '12px', marginBottom: '24px' }} />
          )}

          {/* ── Add Slot form ── */}
          {!isLoading && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
                {t('manage_free_time.add_block')}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_free_time.label_optional')}
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('manage_free_time.label_placeholder')}
                    value={form.label}
                    onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                    {t('manage_commitments.day_label')}
                  </label>
                  <select
                    className="input"
                    value={form.day}
                    onChange={(e) => setForm((f) => ({ ...f, day: e.target.value }))}
                  >
                    {DAYS.map((d) => (
                      <option key={d} value={d}>
                        {t(`student_timetable.days_full.${d}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                      {t('manage_commitments.start_time_label')}
                    </label>
                    <input
                      type="time"
                      className="input"
                      value={form.start_time}
                      onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>
                      {t('manage_commitments.end_time_label')}
                    </label>
                    <input
                      type="time"
                      className="input"
                      value={form.end_time}
                      onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleAdd}
                  style={{ width: '100%' }}
                >
                  {t('manage_free_time.add_to_list')}
                </button>
              </div>
            </div>
          )}

          {/* ── Staged slots list ── */}
          {!isLoading && (
            <div>
              <h2 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 12px' }}>
                {t('manage_free_time.slots_heading', { count: localSlots.length })}
              </h2>

              {localSlots.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center', padding: '24px 0' }}>
                  {t('manage_free_time.empty')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {localSlots.map((slot, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: 'white', borderRadius: '10px', padding: '12px 14px',
                        border: '1px solid var(--border)',
                      }}
                    >
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-dark)' }}>
                          {slot.label ?? t('student_timetable.free_block')}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                          {t(`student_timetable.days_full.${slot.day}`, { defaultValue: slot.day })}
                          {' · '}
                          {slot.start_time}–{slot.end_time}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemove(idx)}
                        aria-label={t('manage_free_time.remove_slot_aria')}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '1.1rem', color: 'var(--danger)', padding: '4px',
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

        {/* ── Sticky save bar ── */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'white', borderTop: '1px solid var(--border)',
          padding: '12px 16px',
        }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ width: '100%' }}
          >
            {isSaving ? t('actions.saving') : t('manage_free_time.save_btn')}
          </button>
        </div>

        {/* ── Toast ── */}
        {toast && (
          <div style={{
            position: 'absolute', bottom: '80px', left: '16px', right: '16px',
            background: 'var(--text-dark)', color: 'white',
            borderRadius: '10px', padding: '12px 16px',
            fontSize: '0.9rem', fontWeight: 500, textAlign: 'center',
            zIndex: 100,
          }}>
            {toast}
          </div>
        )}

      </div>
    </div>
  );
}
